import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import VoiceInputButton from '../../../components/ui/VoiceInputButton';
import { sanitizeInput } from '../../../utils/sanitizer';

const TriggerSelector = ({ selectedTriggers, onToggle, customTrigger, onCustomTriggerChange }) => {
  const triggers = [
    { id: 'movement', label: 'Movement', icon: 'Move' },
    { id: 'rest', label: 'Rest', icon: 'Bed' },
    { id: 'eating', label: 'Eating/Drinking', icon: 'Utensils' },
    { id: 'stress', label: 'Stress/Emotions', icon: 'Frown' },
    { id: 'medication', label: 'Medication', icon: 'Pill' },
    { id: 'time-of-day', label: 'Time of Day', icon: 'Clock' },
    { id: 'not-sure', label: 'Not Sure', icon: 'HelpCircle' },
    { id: 'custom', label: 'Other Trigger', icon: 'Plus' }
  ];

  const handleCustomTriggerChange = (e) => {
    const rawValue = e?.target?.value;
    const sanitized = sanitizeInput(rawValue, {
      maxLength: 200,
      allowHtml: false,
      removeControlChars: false
    });
    onCustomTriggerChange?.(sanitized);
  };

  const handleVoiceChange = (text) => {
    onCustomTriggerChange?.(text);
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          What makes it worse or better?
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Select any triggers you've noticed
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {triggers?.map((trigger) => {
          const isSelected = selectedTriggers?.includes(trigger?.id);
          return (
            <button
              key={trigger?.id}
              onClick={() => onToggle(trigger?.id)}
              className={`
                flex flex-col items-center justify-center p-4 md:p-5 rounded-xl
                border-2 transition-smooth min-h-[100px] md:min-h-[120px]
                ${isSelected 
                  ? 'border-primary bg-primary/5 shadow-therapeutic-md' 
                  : 'border-border bg-card hover:border-primary/30 hover:shadow-therapeutic'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-[0.97]
              `}
              aria-pressed={isSelected}
              aria-label={trigger?.label}
            >
              <div className={`
                flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg mb-3
                ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                transition-smooth
              `}>
                <Icon name={trigger?.icon} size={24} strokeWidth={2} />
              </div>
              <span className={`
                text-sm md:text-base font-medium text-center
                ${isSelected ? 'text-foreground' : 'text-muted-foreground'}
              `}>
                {trigger?.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom Trigger Input */}
      {selectedTriggers?.includes('custom') && (
        <div className="mt-6 bg-card rounded-xl p-4 md:p-6 border border-border space-y-3">
          <div className="flex items-center justify-between">
            <label htmlFor="custom-trigger-input" className="block text-sm md:text-base font-medium text-foreground">
              Describe the trigger
            </label>
            <VoiceInputButton
              value={customTrigger || ''}
              onChange={handleVoiceChange}
              maxLength={200}
            />
          </div>
          
          <input
            id="custom-trigger-input"
            type="text"
            value={customTrigger || ''}
            onChange={handleCustomTriggerChange}
            placeholder="What triggered your symptoms?"
            maxLength={200}
            className="
              w-full px-4 py-3 rounded-lg border border-border bg-background
              text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-3 focus:ring-ring focus:border-ring
              transition-smooth text-sm md:text-base
            "
            aria-label="Custom trigger description"
          />
          <p className="text-xs text-muted-foreground text-right">
            {(customTrigger?.length || 0)}/200
          </p>
        </div>
      )}
    </div>
  );
};

export default TriggerSelector;